// src/pages/Dashboard/Admin/DepensesRecettesDashboard/components/OperationDrawer.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FaExternalLinkAlt,
  FaFileInvoice,
  FaImage,
  FaSave,
  FaTimes,
  FaUpload,
} from "react-icons/fa";
import { PiFilePdfFill } from "react-icons/pi";
import { toast } from "react-toastify";

import AccountingDrawer from "./AccountingDrawer";

import {
  createDepenseRecetteClub,
  getDepensesRecettesClub,
  updateDepenseRecetteClub,
} from "../../../../../services/depensesrecettesclubServices";
import { getSettings } from "../../../../../services/settingsServices";
import { getAllPlanComptables } from "../../../../../services/plancomptableServices";
import {
  toAbsoluteDepensesRecettesPhotoUrl,
  uploadDepensesRecettesPhoto,
} from "../../../../../services/depensesrecettesclubPhotoServices";

import { safeArray, todayStr } from "../utils/accountingDashboardUtils";

const REGLEMENTS = [
  "Virement",
  "Carte bancaire",
  "Espèces",
  "Chèque",
  "Prélèvement",
  "Autre…",
];

const DEFAULT_FORM = {
  saison: "",
  date_operation: todayStr(),
  designation: "",
  categorie: "",
  beneficiaire: "",
  montant: "",
  type_reglement: "",
  type_operation: "Dépense",
  photo_url: "",
  lien_url: "",
  plan_comptable_id: "",
};

const DEFAULT_PHOTO = "/img/no-image.jpg";

function neighborsFromSeason(season) {
  const match = /^(\d{4})\s*-\s*(\d{4})$/.exec(season || "");

  if (!match) {
    return {
      prev: season || "",
      current: season || "",
      next: season || "",
    };
  }

  const start = parseInt(match[1], 10);
  const end = parseInt(match[2], 10);

  return {
    prev: `${start - 1}-${end - 1}`,
    current: `${start}-${end}`,
    next: `${start + 1}-${end + 1}`,
  };
}

function toDateInputValue(value) {
  if (!value) return todayStr();

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().slice(0, 10);
  }

  const text = String(value);
  return text.length >= 10 ? text.slice(0, 10) : todayStr();
}

function isPdfHref(value = "") {
  return /\.pdf(\?|$)/i.test(String(value || "").trim());
}

function withBust(url, version) {
  if (!url) return url;
  return `${url}${url.includes("?") ? "&" : "?"}v=${version}`;
}

function buildInitialForm(operation, mode) {
  if (mode !== "edit" || !operation) {
    return { ...DEFAULT_FORM };
  }

  return {
    saison: operation?.saison || "",
    date_operation: toDateInputValue(operation?.date_operation),
    designation: operation?.designation || "",
    categorie: operation?.categorie || "",
    beneficiaire: operation?.beneficiaire || "",
    montant:
      operation?.montant === null || operation?.montant === undefined
        ? ""
        : String(operation.montant),
    type_reglement: operation?.type_reglement || "",
    type_operation: operation?.type_operation || "Dépense",
    photo_url: operation?.photo_url || "",
    lien_url: operation?.lien_url || "",
    plan_comptable_id:
      operation?.plan_comptable_id === null ||
      operation?.plan_comptable_id === undefined
        ? ""
        : String(operation.plan_comptable_id),
  };
}

function FieldLabel({ children }) {
  return (
    <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
      {children}
    </label>
  );
}

function TextInput(props) {
  return (
    <input
      {...props}
      className={[
        "w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-900 outline-none transition focus:border-indigo-300 focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:focus:border-indigo-500 dark:focus:ring-indigo-500/10",
        props.className || "",
      ].join(" ")}
    />
  );
}

function SelectInput(props) {
  return (
    <select
      {...props}
      className={[
        "w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-900 outline-none transition focus:border-indigo-300 focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:focus:border-indigo-500 dark:focus:ring-indigo-500/10",
        props.className || "",
      ].join(" ")}
    />
  );
}

export default function OperationDrawer({
  open,
  mode = "create",
  operation,
  onClose,
  onSaved,
}) {
  const formRef = useRef(null);

  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [initialForm, setInitialForm] = useState(DEFAULT_FORM);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [seasonOptions, setSeasonOptions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [planComptables, setPlanComptables] = useState([]);

  const [useNewCategorie, setUseNewCategorie] = useState(false);
  const [newCategorie, setNewCategorie] = useState("");

  const [useOtherReglement, setUseOtherReglement] = useState(false);
  const [otherReglement, setOtherReglement] = useState("");

  const [selectedFile, setSelectedFile] = useState(null);
  const [outputFormat, setOutputFormat] = useState("webp");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewVersion, setPreviewVersion] = useState(0);

  const isEdit = mode === "edit";
  const title = isEdit ? "Modifier l’opération" : "Ajouter une opération";
  const subtitle = isEdit
    ? "Modifiez les informations, le compte comptable ou le justificatif."
    : "Créez une nouvelle dépense ou recette du club.";

  const loadContext = useCallback(async (recordSeason = "") => {
    setLoading(true);

    try {
      const [settingsResponse, operationsResponse, plansResponse] =
        await Promise.all([
          getSettings(),
          getDepensesRecettesClub(),
          getAllPlanComptables(),
        ]);

      const activeSeason = safeArray(settingsResponse)?.[0]?.saison_active || "";
      const aroundActive = neighborsFromSeason(activeSeason || recordSeason);
      const aroundRecord = neighborsFromSeason(recordSeason || activeSeason);

      const seasons = Array.from(
        new Set(
          [
            aroundActive.prev,
            aroundActive.current,
            aroundActive.next,
            aroundRecord.prev,
            aroundRecord.current,
            aroundRecord.next,
          ].filter(Boolean)
        )
      );

      const existingCategories = Array.from(
        new Set(
          safeArray(operationsResponse)
            .map((item) => item?.categorie)
            .filter(Boolean)
        )
      ).sort((a, b) => a.localeCompare(b, "fr"));

      const sortedPlans = safeArray(plansResponse).sort((a, b) => {
        if (a?.sens === b?.sens) {
          return Number(a?.ordre || 0) - Number(b?.ordre || 0);
        }

        return a?.sens === "RECETTE" ? -1 : 1;
      });

      setSeasonOptions(seasons);
      setCategories(existingCategories);
      setPlanComptables(sortedPlans);

      setFormData((prev) => ({
        ...prev,
        saison: prev.saison || aroundActive.current || activeSeason || "",
        date_operation: prev.date_operation || todayStr(),
      }));
    } catch (error) {
      console.error("[OperationDrawer] load context error:", error);
      toast.error(
        "Impossible de charger la configuration saison / catégories / plan comptable."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;

    const initial = buildInitialForm(operation, mode);

    setInitialForm(initial);
    setFormData(initial);

    setUseNewCategorie(false);
    setNewCategorie("");
    setUseOtherReglement(false);
    setOtherReglement("");
    setSelectedFile(null);
    setUploadProgress(0);
    setPreviewVersion((value) => value + 1);

    loadContext(initial.saison || "");
  }, [open, mode, operation, loadContext]);

  useEffect(() => {
    if (!open || !categories.length) return;

    if (formData.categorie && !categories.includes(formData.categorie)) {
      setUseNewCategorie(true);
      setNewCategorie(formData.categorie);
    }
  }, [open, categories, formData.categorie]);

  useEffect(() => {
    if (!open) return;

    if (formData.type_reglement && !REGLEMENTS.includes(formData.type_reglement)) {
      setUseOtherReglement(true);
      setOtherReglement(formData.type_reglement);
    }
  }, [open, formData.type_reglement]);

  const sensWanted = formData.type_operation === "Recette" ? "RECETTE" : "DEPENSE";

  const filteredPlans = useMemo(() => {
    return planComptables.filter((plan) => plan?.sens === sensWanted);
  }, [planComptables, sensWanted]);

  useEffect(() => {
    if (!formData.plan_comptable_id) return;

    const currentPlan = planComptables.find(
      (plan) => String(plan?.id) === String(formData.plan_comptable_id)
    );

    if (currentPlan && currentPlan.sens !== sensWanted) {
      setFormData((prev) => ({
        ...prev,
        plan_comptable_id: "",
      }));
    }
  }, [formData.plan_comptable_id, planComptables, sensWanted]);

  const isDirty = useMemo(() => {
    return JSON.stringify(formData) !== JSON.stringify(initialForm);
  }, [formData, initialForm]);

  const canSubmit = useMemo(() => {
    const hasCategorie = useNewCategorie
      ? Boolean(newCategorie.trim())
      : Boolean(formData.categorie);

    const hasReglement = useOtherReglement
      ? Boolean(otherReglement.trim())
      : Boolean(formData.type_reglement);

    return (
      !loading &&
      !submitting &&
      Boolean(formData.saison) &&
      Boolean(formData.date_operation) &&
      Boolean(formData.designation?.trim()) &&
      hasCategorie &&
      formData.montant !== "" &&
      !Number.isNaN(Number(formData.montant)) &&
      hasReglement
    );
  }, [
    formData,
    loading,
    newCategorie,
    otherReglement,
    submitting,
    useNewCategorie,
    useOtherReglement,
  ]);

  const requestClose = () => {
    if (submitting || uploading) return;

    if (
      isDirty &&
      !window.confirm("Des modifications ne sont pas enregistrées. Fermer ?")
    ) {
      return;
    }

    onClose?.();
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    if (name === "categorie") {
      if (value === "__new__") {
        setUseNewCategorie(true);
        setFormData((prev) => ({ ...prev, categorie: "" }));
      } else {
        setUseNewCategorie(false);
        setNewCategorie("");
        setFormData((prev) => ({ ...prev, categorie: value }));
      }
      return;
    }

    if (name === "type_reglement") {
      if (value === "__other__") {
        setUseOtherReglement(true);
        setFormData((prev) => ({ ...prev, type_reglement: "" }));
      } else {
        setUseOtherReglement(false);
        setOtherReglement("");
        setFormData((prev) => ({ ...prev, type_reglement: value }));
      }
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const preventEnterSubmit = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const buildPayload = () => ({
    ...formData,
    categorie: useNewCategorie ? newCategorie.trim() : formData.categorie,
    type_reglement: useOtherReglement
      ? otherReglement.trim()
      : formData.type_reglement,
    montant: Number(formData.montant),
    plan_comptable_id: formData.plan_comptable_id
      ? Number(formData.plan_comptable_id)
      : null,
  });

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!canSubmit) return;

    setSubmitting(true);

    try {
      const payload = buildPayload();

      if (isEdit) {
        await updateDepenseRecetteClub(operation.id, payload);
        toast.success("Opération mise à jour.");
      } else {
        await createDepenseRecetteClub(payload);
        toast.success("Opération créée.");
      }

      await onSaved?.();
      onClose?.();
    } catch (error) {
      console.error("[OperationDrawer] submit error:", error);
      toast.error(
        isEdit
          ? "Erreur lors de la mise à jour de l’opération."
          : "Erreur lors de la création de l’opération."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpload = async () => {
    if (!isEdit || !operation?.id) {
      toast.info("Enregistrez d’abord l’opération avant d’ajouter un fichier.");
      return;
    }

    if (!selectedFile) {
      toast.warn("Sélectionnez un fichier image ou PDF.");
      return;
    }

    const validType =
      /^image\/(png|jpe?g|webp)$/i.test(selectedFile.type) ||
      selectedFile.type === "application/pdf";

    if (!validType) {
      toast.error("Format non supporté. Utilisez PNG, JPG, WEBP ou PDF.");
      return;
    }

    if (selectedFile.size > 10 * 1024 * 1024) {
      toast.error("Fichier trop volumineux. Maximum 10 Mo.");
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);

      const params =
        selectedFile.type === "application/pdf" ? {} : { format: outputFormat };

      const updated = await uploadDepensesRecettesPhoto(
        operation.id,
        selectedFile,
        {
          ...params,
          onUploadProgress: (event) => {
            if (!event.total) return;
            setUploadProgress(Math.round((event.loaded / event.total) * 100));
          },
        }
      );

      setFormData((prev) => ({
        ...prev,
        photo_url: updated?.photo_url || prev.photo_url,
      }));

      setPreviewVersion((value) => value + 1);
      setSelectedFile(null);

      await onSaved?.();

      toast.success("Justificatif téléversé.");
    } catch (error) {
      console.error("[OperationDrawer] upload error:", error);
      toast.error("Erreur lors du téléversement du justificatif.");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const previewUrl = formData.photo_url?.trim()
    ? toAbsoluteDepensesRecettesPhotoUrl(formData.photo_url)
    : DEFAULT_PHOTO;

  const previewWithBust = withBust(previewUrl, previewVersion);
  const previewIsPdf = isPdfHref(formData.photo_url);

  const footer = (
    <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
      <button
        type="button"
        onClick={requestClose}
        disabled={submitting || uploading}
        className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-100 px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
      >
        <FaTimes />
        Annuler
      </button>

      <button
        type="button"
        onClick={() => formRef.current?.requestSubmit()}
        disabled={!canSubmit}
        className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 py-2 text-sm font-bold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
      >
        <FaSave />
        {submitting
          ? "Enregistrement..."
          : isEdit
            ? "Enregistrer les modifications"
            : "Créer l’opération"}
      </button>
    </div>
  );

  return (
    <AccountingDrawer
      open={open}
      onClose={requestClose}
      title={title}
      subtitle={subtitle}
      size="xl"
      footer={footer}
    >
      <form ref={formRef} onSubmit={handleSubmit} className="space-y-5">
        <section className="overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

          <div className="grid gap-4 p-4 sm:grid-cols-2 sm:p-5">
            <div>
              <FieldLabel>Saison</FieldLabel>
              <SelectInput
                name="saison"
                value={formData.saison}
                onChange={handleChange}
                disabled={loading}
                required
              >
                <option value="">
                  {loading ? "Chargement..." : "— Sélectionner —"}
                </option>
                {seasonOptions.map((season) => (
                  <option key={season} value={season}>
                    {season}
                  </option>
                ))}
              </SelectInput>
            </div>

            <div>
              <FieldLabel>Date de l’opération</FieldLabel>
              <TextInput
                type="date"
                name="date_operation"
                value={formData.date_operation}
                onChange={handleChange}
                required
              />
            </div>

            <div>
              <FieldLabel>Type d’opération</FieldLabel>
              <SelectInput
                name="type_operation"
                value={formData.type_operation}
                onChange={handleChange}
                required
              >
                <option value="Dépense">Dépense</option>
                <option value="Recette">Recette</option>
              </SelectInput>
            </div>

            <div>
              <FieldLabel>Montant</FieldLabel>
              <TextInput
                type="number"
                name="montant"
                value={formData.montant}
                onChange={handleChange}
                min="0"
                step="0.01"
                placeholder="0.00"
                required
              />
            </div>

            <div className="sm:col-span-2">
              <FieldLabel>Désignation</FieldLabel>
              <TextInput
                type="text"
                name="designation"
                value={formData.designation}
                onChange={handleChange}
                onKeyDown={preventEnterSubmit}
                placeholder="Ex : Cotisations perçues, achat matériel, subvention..."
                required
              />
            </div>

            <div>
              <FieldLabel>Bénéficiaire</FieldLabel>
              <TextInput
                type="text"
                name="beneficiaire"
                value={formData.beneficiaire}
                onChange={handleChange}
                onKeyDown={preventEnterSubmit}
                placeholder="Club, mairie, fournisseur, adhérents..."
              />
            </div>

            <div>
              <FieldLabel>Catégorie</FieldLabel>
              <SelectInput
                name="categorie"
                value={useNewCategorie ? "__new__" : formData.categorie}
                onChange={handleChange}
                required={!useNewCategorie}
              >
                <option value="">— Sélectionner —</option>
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
                <option value="__new__">+ Nouvelle catégorie</option>
              </SelectInput>

              {useNewCategorie && (
                <TextInput
                  type="text"
                  value={newCategorie}
                  onChange={(event) => setNewCategorie(event.target.value)}
                  onKeyDown={preventEnterSubmit}
                  placeholder="Nom de la nouvelle catégorie"
                  className="mt-2"
                  required
                />
              )}
            </div>

            <div>
              <FieldLabel>Type de règlement</FieldLabel>
              <SelectInput
                name="type_reglement"
                value={useOtherReglement ? "__other__" : formData.type_reglement}
                onChange={handleChange}
                required={!useOtherReglement}
              >
                <option value="">— Sélectionner —</option>
                {REGLEMENTS.map((reglement) => (
                  <option
                    key={reglement}
                    value={reglement === "Autre…" ? "__other__" : reglement}
                  >
                    {reglement}
                  </option>
                ))}
              </SelectInput>

              {useOtherReglement && (
                <TextInput
                  type="text"
                  value={otherReglement}
                  onChange={(event) => setOtherReglement(event.target.value)}
                  onKeyDown={preventEnterSubmit}
                  placeholder="Précisez le type de règlement"
                  className="mt-2"
                  required
                />
              )}
            </div>

            <div>
              <FieldLabel>Compte comptable</FieldLabel>
              <SelectInput
                name="plan_comptable_id"
                value={formData.plan_comptable_id}
                onChange={handleChange}
              >
                <option value="">— Aucun compte —</option>
                {filteredPlans.map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.code} — {plan.intitule}
                  </option>
                ))}
              </SelectInput>

              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                Le compte est filtré selon le type : dépense ou recette.
              </p>
            </div>
          </div>
        </section>

        <section className="overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-violet-500 via-fuchsia-500 to-rose-500" />

          <div className="p-4 sm:p-5">
            <div className="mb-4">
              <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                Justificatifs
              </p>

              <h3 className="mt-1 flex items-center gap-2 text-lg font-bold text-slate-900 dark:text-white">
                <FaFileInvoice />
                Pièce, facture ou lien externe
              </h3>
            </div>

            <div className="grid gap-4 lg:grid-cols-[220px_1fr]">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-950">
                {formData.photo_url && previewIsPdf ? (
                  <a
                    href={previewWithBust}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex aspect-square items-center justify-center rounded-2xl bg-rose-50 text-rose-700 ring-1 ring-rose-100 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/20"
                  >
                    <PiFilePdfFill className="text-5xl" />
                  </a>
                ) : (
                  <img
                    src={previewWithBust}
                    alt="Justificatif"
                    className="aspect-square w-full rounded-2xl object-cover ring-1 ring-slate-200 dark:ring-slate-800"
                    onError={(event) => {
                      event.currentTarget.onerror = null;
                      event.currentTarget.src = DEFAULT_PHOTO;
                    }}
                  />
                )}

                {formData.photo_url && (
                  <a
                    href={previewWithBust}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-slate-950 px-3 py-2 text-xs font-bold text-white transition hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
                  >
                    <FaExternalLinkAlt />
                    Ouvrir
                  </a>
                )}
              </div>

              <div className="space-y-4">
                <div>
                  <FieldLabel>URL du justificatif</FieldLabel>
                  <TextInput
                    type="url"
                    name="photo_url"
                    value={formData.photo_url}
                    onChange={handleChange}
                    placeholder="https://... ou /uploads/..."
                  />
                </div>

                <div>
                  <FieldLabel>Lien externe</FieldLabel>
                  <TextInput
                    type="url"
                    name="lien_url"
                    value={formData.lien_url}
                    onChange={handleChange}
                    placeholder="Lien facture, Drive, HelloAsso..."
                  />
                </div>

                {isEdit ? (
                  <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-950">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                      <div className="flex-1">
                        <FieldLabel>Téléverser un fichier</FieldLabel>
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/webp,application/pdf"
                          onChange={(event) =>
                            setSelectedFile(event.target.files?.[0] || null)
                          }
                          className="block w-full cursor-pointer rounded-2xl border border-slate-200 bg-white text-sm text-slate-600 file:mr-3 file:border-0 file:bg-slate-950 file:px-4 file:py-2.5 file:text-sm file:font-bold file:text-white dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:file:bg-white dark:file:text-slate-950"
                        />
                      </div>

                      {selectedFile &&
                        selectedFile.type !== "application/pdf" && (
                          <div className="w-full sm:w-40">
                            <FieldLabel>Format</FieldLabel>
                            <SelectInput
                              value={outputFormat}
                              onChange={(event) =>
                                setOutputFormat(event.target.value)
                              }
                            >
                              <option value="webp">WEBP</option>
                              <option value="jpg">JPG</option>
                              <option value="png">PNG</option>
                            </SelectInput>
                          </div>
                        )}

                      <button
                        type="button"
                        onClick={handleUpload}
                        disabled={!selectedFile || uploading}
                        className="inline-flex items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <FaUpload />
                        {uploading ? `${uploadProgress}%` : "Téléverser"}
                      </button>
                    </div>

                    <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                      Formats acceptés : PNG, JPG, WEBP ou PDF. Taille maximale
                      recommandée : 10 Mo.
                    </p>
                  </div>
                ) : (
                  <div className="rounded-2xl bg-amber-50 p-4 text-sm text-amber-800 ring-1 ring-amber-100 dark:bg-amber-500/10 dark:text-amber-200 dark:ring-amber-500/20">
                    <strong>Astuce :</strong> créez d’abord l’opération, puis
                    ouvrez-la en édition pour téléverser un justificatif image
                    ou PDF.
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </form>
    </AccountingDrawer>
  );
}